import type { AuthPortal, AuthUser } from '../../api/auth/authApi.ts';
import { AUTH_PORTAL, IS_INDEPENDENT_PORTAL } from '../../app/portal.ts';
import type { UserInfo } from '../../types/common/common.ts';
import type { StoredSession } from './persistence.ts';

export interface LoginCredentials {
  userName: string;
  password: string;
  portal?: AuthPortal;
}

export function toUserInfo(user: AuthUser): UserInfo {
  const roles = user.roles ?? [];
  const legacyAdministrator = user.portal === 'QCDOC' && roles.some((role) =>
    /^Administrator(?: - (?:Baroid|Cementing|PE|Production Enhancement|Multi-Chem))?$/i.test(role));
  return {
    sub: user.userId,
    email: user.email ?? '',
    name: user.fullName || user.userName,
    preferredUsername: user.userName,
    groups: legacyAdministrator ? [...new Set([...roles, 'Admin'])] : roles,
    psl: user.psl ?? undefined,
    portal: user.portal,
    packaging: user.packaging,
    isInternal: user.isInternal,
    company: user.company ?? undefined,
    accessProfile: user.accessProfile,
  };
}

export async function login(credentials: LoginCredentials): Promise<StoredSession> {
  const api = await import('../../api/auth/authApi.ts');
  const result = await api.login({ ...credentials, portal: IS_INDEPENDENT_PORTAL ? AUTH_PORTAL : credentials.portal ?? api.AuthPortals.QCDoc });
  return { token: result.accessToken, expiresAt: result.expiresAt, user: toUserInfo(result.user) };
}

export async function logout(): Promise<void> {
  const api = await import('../../api/auth/authApi.ts');
  await api.logout();
}

export async function getCurrentUser(): Promise<UserInfo> {
  const api = await import('../../api/auth/authApi.ts');
  return toUserInfo(await api.getCurrentUser());
}